import React, { useState, useEffect } from "react";
import {
  Download,
  FileText,
  FileSpreadsheet,
  MessageSquare,
  Eye,
} from "lucide-react";
import jsPDF from "jspdf"; // FIX 7: default import
import autoTable from "jspdf-autotable";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  server_post_data,
  get_contact_us,
} from "../ServiceConnection/serviceconnection.js";

// Outside component — no recreation on re-render
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function Contact() {
  const [contacts, setContacts] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  /* ---------------- FETCH ---------------- */

  // FIX 2: Plain async — no useCallback
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const responsesss = await server_post_data(
        get_contact_us,
        new FormData(),
      );
      const response = responsesss.data;
      
      // FIX 1: Correct status check — backend returns {status:1, data:[...]}
      if (response.status === 1) {
        console.log(response.data);
        setContacts(Array.isArray(response.data) ? response.data : []);
      } else {
        setContacts([]);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  /* ---------------- FILTER ---------------- */

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
  };

  const filtered = contacts.filter((c) => {
    const d = new Date(c.entry_date);
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
    return true;
  });

  /* ---------------- EXPORT ROWS ---------------- */

  const exportRows = filtered.map((c, i) => ({
    "S.No": i + 1,
    Date: formatDate(c.entry_date),
    Name: c.full_name || "-",
    "Phone no": c.phone_number || "-",
    Message: c.message || "-",
  }));

  /* CSV */
  const downloadCSV = () => {
    if (!filtered.length) return; // FIX 6
    const csv = Papa.unparse(exportRows);
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "contacts.csv";
    link.click();
  };

  /* PDF — FIX 3: autoTable for proper pagination */
  const downloadPDF = () => {
    if (!filtered.length) return; // FIX 6
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Contact Messages Report", 14, 20);

    autoTable(doc, {
      startY: 30,
      head: [["S.No", "Date", "Name", "Phone no", "Message"]],
      body: exportRows.map((r) => [
        r["S.No"],
        r["Date"],
        r["Name"],
        r["Phone no"],
        r["Message"],
      ]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [139, 92, 246] },
      columnStyles: { 4: { cellWidth: 70 } }, // Message column wider
    });

    doc.save("contacts.pdf");
  };

  /* EXCEL */
  const downloadExcel = () => {
    if (!filtered.length) return; // FIX 6
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");
    XLSX.writeFile(wb, "contacts.xlsx");
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="pl-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contact Us</h1>
        <p className="text-gray-500">View and manage contact messages</p>
      </div>

      <div className="mb-6 rounded-xl border bg-white p-4 shadow-sm space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          {/* DATE FILTER */}
          <div className="flex items-end gap-4">
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm w-36"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm w-36"
              />
            </div>
            <button
              onClick={clearFilters}
              className="border rounded-md px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 h-[32px]"
            >
              Clear
            </button>
          </div>

          {/* SUMMARY CARD */}
          <div className="flex items-center gap-3 bg-white border rounded-xl px-5 py-3 shadow-sm">
            <div className="bg-purple-100 p-2 rounded-lg">
              <MessageSquare size={18} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Messages</p>
              <p className="text-lg font-bold">{filtered.length}</p>
            </div>
          </div>
        </div>

        {/* EXPORT BUTTONS — FIX 6: disabled when empty */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={downloadCSV}
            disabled={!filtered.length}
            className="flex items-center gap-1 border rounded-md px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={14} /> CSV
          </button>
          <button
            onClick={downloadPDF}
            disabled={!filtered.length}
            className="flex items-center gap-1 bg-pink-500 text-white rounded-md px-3 py-1.5 text-xs hover:bg-pink-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileText size={14} /> PDF
          </button>
          <button
            onClick={downloadExcel}
            disabled={!filtered.length}
            className="flex items-center gap-1 bg-green-500 text-white rounded-md px-3 py-1.5 text-xs hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet size={14} /> Excel
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {["S.No", "Date", "Name", "Phone no", "Message", "View"].map(
                  (h) => (
                    <th
                      key={h}
                      className={`px-4 py-3 text-xs font-semibold uppercase text-gray-500 ${
                        h === "View" ? "text-center" : "text-left"
                      }`}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>

            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-6 text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-6 text-gray-400">
                    No messages found
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  // FIX 4: key = primary_id, not index
                  <tr key={c.primary_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{filtered.indexOf(c) + 1}</td>
                    <td className="px-4 py-3">{formatDate(c.entry_date)}</td>
                    <td className="px-4 py-3 font-medium">
                      {c.full_name || "-"}
                    </td>
                    <td className="px-4 py-3">{c.phone_number || "-"}</td>
                    {/* FIX 5: null-safe message truncate */}
                    <td className="px-4 py-3">
                      {(c.message || "").length > 50
                        ? c.message.slice(0, 50) + "..."
                        : c.message || "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setSelectedMessage(c.message || "")}
                        className="p-2 rounded-lg hover:bg-gray-100 transition"
                      >
                        <Eye size={18} className="text-blue-600" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MESSAGE MODAL */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-4">Full Message</h2>
            <p className="text-gray-700 whitespace-pre-line">
              {selectedMessage}
            </p>
            <div className="mt-6 text-right">
              <button
                onClick={() => setSelectedMessage("")}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
